/**
 * ContextualNarrativePanel Component
 * Enhanced AI narrative display with proactive insights and decision support
 * Designed to connect with users through engaging, actionable content
 */

import { useState, useMemo } from 'react'
import {
  Brain,
  AlertTriangle,
  Lightbulb,
  TrendingUp,
  TrendingDown,
  Target,
  Clock,
  ChevronRight,
  Zap,
  Shield,
  Wrench,
  ArrowRight,
  CheckCircle,
  Info
} from 'lucide-react'
import clsx from 'clsx'

interface InsightItem {
  type: 'critical' | 'warning' | 'opportunity' | 'info'
  title: string
  description: string
  action?: {
    label: string
    onClick?: () => void
    href?: string
  }
  metric?: {
    label: string
    value: string | number
    trend?: 'up' | 'down' | 'stable'
  }
}

interface DecisionOption {
  id: string
  label: string
  description: string
  impact: 'high' | 'medium' | 'low'
  effort: 'high' | 'medium' | 'low'
  recommended?: boolean
  onClick?: () => void
}

interface ContextualNarrativePanelProps {
  title: string
  summary: string
  insights: InsightItem[]
  decisions?: DecisionOption[]
  confidence: number
  lastUpdated?: string
  assetId?: string
  category?: 'analysis' | 'prediction' | 'recommendation' | 'alert'
  className?: string
}

export default function ContextualNarrativePanel({
  title,
  summary,
  insights,
  decisions,
  confidence,
  lastUpdated,
  assetId,
  category = 'analysis',
  className
}: ContextualNarrativePanelProps) {
  const [expandedInsight, setExpandedInsight] = useState<number | null>(null)
  const [showDecisions, setShowDecisions] = useState(false)

  const getCategoryIcon = () => {
    switch (category) {
      case 'prediction':
        return <TrendingUp size={20} className="text-primary-400" />
      case 'recommendation':
        return <Lightbulb size={20} className="text-status-healthy" />
      case 'alert':
        return <AlertTriangle size={20} className="text-status-warning" />
      default:
        return <Brain size={20} className="text-primary-400" />
    }
  }

  const getCategoryStyle = () => {
    switch (category) {
      case 'prediction':
        return 'border-primary-500/30 bg-gradient-to-br from-primary-500/10 to-transparent'
      case 'recommendation':
        return 'border-status-healthy/30 bg-gradient-to-br from-status-healthy/10 to-transparent'
      case 'alert':
        return 'border-status-warning/30 bg-gradient-to-br from-status-warning/10 to-transparent'
      default:
        return 'border-surface-600 bg-gradient-to-br from-surface-800 to-transparent'
    }
  }

  const getInsightIcon = (type: InsightItem['type']) => {
    switch (type) {
      case 'critical':
        return <AlertTriangle size={16} className="text-status-critical" />
      case 'warning':
        return <Shield size={16} className="text-status-warning" />
      case 'opportunity':
        return <Zap size={16} className="text-status-healthy" />
      default:
        return <Info size={16} className="text-primary-400" />
    }
  }

  const getInsightStyle = (type: InsightItem['type']) => {
    switch (type) {
      case 'critical':
        return 'border-status-critical/30 bg-status-critical/5'
      case 'warning':
        return 'border-status-warning/30 bg-status-warning/5'
      case 'opportunity':
        return 'border-status-healthy/30 bg-status-healthy/5'
      default:
        return 'border-surface-600 bg-surface-800/50'
    }
  }

  const getImpactColor = (impact: DecisionOption['impact']) => {
    switch (impact) {
      case 'high':
        return 'text-status-healthy'
      case 'medium':
        return 'text-status-warning'
      default:
        return 'text-surface-400'
    }
  }

  const getEffortColor = (effort: DecisionOption['effort']) => {
    switch (effort) {
      case 'high':
        return 'text-status-critical'
      case 'medium':
        return 'text-status-warning'
      default:
        return 'text-status-healthy'
    }
  }

  const prioritizedInsights = useMemo(() => {
    const priority = { critical: 0, warning: 1, opportunity: 2, info: 3 }
    return [...insights].sort((a, b) => priority[a.type] - priority[b.type])
  }, [insights])

  const criticalCount = insights.filter(i => i.type === 'critical').length
  const warningCount = insights.filter(i => i.type === 'warning').length

  return (
    <div className={clsx('rounded-xl border overflow-hidden', getCategoryStyle(), className)}>
      {/* Header with Urgency Indicator */}
      <div className="p-4 border-b border-surface-700/50">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">{getCategoryIcon()}</div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-surface-100">{title}</h3>
                {assetId && (
                  <span className="text-xs font-mono px-2 py-0.5 rounded bg-primary-500/10 text-primary-400">
                    {assetId}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4 mt-1 text-xs text-surface-400">
                <span className="flex items-center gap-1">
                  <Target size={12} />
                  {Math.round(confidence * 100)}% confidence
                </span>
                {lastUpdated && (
                  <span className="flex items-center gap-1">
                    <Clock size={12} />
                    {lastUpdated}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Urgency Badges */}
          <div className="flex items-center gap-2">
            {criticalCount > 0 && (
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-status-critical/20 text-status-critical border border-status-critical/30">
                {criticalCount} Critical
              </span>
            )}
            {warningCount > 0 && (
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-status-warning/20 text-status-warning border border-status-warning/30">
                {warningCount} Warning
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Summary - The Story */}
      <div className="p-4 bg-surface-900/30">
        <div className="prose prose-sm prose-invert max-w-none">
          <p className="text-surface-300 leading-relaxed whitespace-pre-line">{summary}</p>
        </div>
      </div>

      {/* Proactive Insights */}
      {prioritizedInsights.length > 0 && (
        <div className="p-4 border-t border-surface-700/50">
          <h4 className="text-xs font-semibold text-surface-400 uppercase mb-3 flex items-center gap-2">
            <Zap size={12} />
            What You Need to Know
          </h4>
          <div className="space-y-2">
            {prioritizedInsights.map((insight, index) => (
              <div
                key={index}
                className={clsx(
                  'rounded-lg border p-3 transition-all cursor-pointer',
                  getInsightStyle(insight.type),
                  expandedInsight === index && 'ring-1 ring-primary-500/50'
                )}
                onClick={() => setExpandedInsight(expandedInsight === index ? null : index)}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">{getInsightIcon(insight.type)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h5 className="font-medium text-surface-200 text-sm">{insight.title}</h5>
                      {insight.metric && (
                        <div className="flex items-center gap-1 text-xs">
                          <span className="text-surface-400">{insight.metric.label}:</span>
                          <span className="font-mono text-surface-200">{insight.metric.value}</span>
                          {insight.metric.trend === 'up' && <TrendingUp size={12} className="text-status-healthy" />}
                          {insight.metric.trend === 'down' && <TrendingDown size={12} className="text-status-critical" />}
                        </div>
                      )}
                    </div>

                    {/* Expanded Content */}
                    <div className={clsx(
                      'overflow-hidden transition-all',
                      expandedInsight === index ? 'max-h-40 mt-2' : 'max-h-0'
                    )}>
                      <p className="text-sm text-surface-400">{insight.description}</p>
                      {insight.action && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            insight.action?.onClick?.()
                          }}
                          className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary-400 hover:text-primary-300"
                        >
                          {insight.action.label}
                          <ArrowRight size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                  <ChevronRight
                    size={16}
                    className={clsx(
                      'text-surface-500 transition-transform',
                      expandedInsight === index && 'rotate-90'
                    )}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Decision Support */}
      {decisions && decisions.length > 0 && (
        <div className="p-4 border-t border-surface-700/50 bg-surface-900/50">
          <button
            onClick={() => setShowDecisions(!showDecisions)}
            className="w-full flex items-center justify-between text-left"
          >
            <h4 className="text-xs font-semibold text-surface-400 uppercase flex items-center gap-2">
              <Wrench size={12} />
              Recommended Actions
            </h4>
            <ChevronRight
              size={16}
              className={clsx(
                'text-surface-500 transition-transform',
                showDecisions && 'rotate-90'
              )}
            />
          </button>

          <div className={clsx(
            'overflow-hidden transition-all',
            showDecisions ? 'max-h-96 mt-4' : 'max-h-0'
          )}>
            <div className="space-y-3">
              {decisions.map((decision) => (
                <div
                  key={decision.id}
                  className={clsx(
                    'p-3 rounded-lg border transition-all',
                    decision.recommended
                      ? 'border-primary-500/50 bg-primary-500/5'
                      : 'border-surface-700 bg-surface-800/50',
                    decision.onClick && 'cursor-pointer hover:border-primary-500/30'
                  )}
                  onClick={decision.onClick}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-2">
                      {decision.recommended && (
                        <CheckCircle size={16} className="text-primary-400 mt-0.5" />
                      )}
                      <div>
                        <h5 className="font-medium text-surface-200 text-sm flex items-center gap-2">
                          {decision.label}
                          {decision.recommended && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-primary-500/20 text-primary-400">
                              Recommended
                            </span>
                          )}
                        </h5>
                        <p className="text-xs text-surface-400 mt-1">{decision.description}</p>
                      </div>
                    </div>
                    <div className="text-right text-xs">
                      <div className="flex items-center gap-1">
                        <span className="text-surface-500">Impact:</span>
                        <span className={getImpactColor(decision.impact)}>{decision.impact}</span>
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        <span className="text-surface-500">Effort:</span>
                        <span className={getEffortColor(decision.effort)}>{decision.effort}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
